// @ts-strict-ignore
import { createSelector } from 'reselect';
import cloneDeep from 'lodash/cloneDeep';
import type {
  Graph,
} from 'flow/service/graph';
import {
  adjustLayoutToFreezeFixedNodes,
  getAllNodeIdsConnectedToNode,
  getMaxPathLengthMap,
  getBidirectionalGraphTopology,
  layOutGraph,
  adjustConstructionNodes,
  appendAdditionalGraphElements,
  buildGraph,
  shouldAddSkeletonNodeAndEdge,
  organizeContainerNodes,
  countNodesByType,
  getFolderSize,
  filterNodesByLineage,
  filterEdgesByLineage,
  filterOutUnsetTagNodes,
  filterOutUnsetTagEdges,
  appendFolderNode,
  buildSnippetSubgraphs,
  buildSnippetSubgraphTopolgies,
  buildSnippetSubgraphMaxPathMaps,
  buildSnippetSubgraphIncomersMap,
  adjustSnippetChildIncomers,
  adjustEdgesForSnippetNodes,
  buildSubgraphDimensions,
  FOLDER_NODE_ID,
} from 'flow/service/graph';
import type { State } from '~/store';
import { selectIsEditingLinkedChildAction, selectNodeConnectorState } from './general';
import {
  selectNodeContainerState,
  selectSnippetCollapsedStates,
  selectNodeFolderState,
  selectShouldRenderCriticalNodePath,
} from './ui';
import type { PublicId } from 'flow/store/engine/types';
import { defaultNodeDimensions } from 'flow/service/graph/layout/nodeMatrix';

export const selectGraph = createSelector(
  [
    (state: State) => state.workflowEditor.workflowData?.actions,
    (state: State) => state.workflowEditor.workflowData?.dependencies,
    (state: State) => state.workflowEditor.workflowProps,
  ],
  (actions, dependencies, workflowProps) => buildGraph(actions, dependencies, workflowProps),
);

export const selectHasNodeContainers = createSelector(
  [
    selectGraph,
  ],
  graph => {
    const {
      inputAttribute: inputAttributeCount,
      snippet: snippetCount,
    } = countNodesByType(graph.nodes);

    return { hasNodeFolder: inputAttributeCount > 1, hasSnippets: snippetCount > 0 };
  },
);

const selectGraphWithAdditionalElements = createSelector(
  [
    selectGraph,
    selectNodeConnectorState,
    selectNodeContainerState,
    selectIsEditingLinkedChildAction,
    selectHasNodeContainers,
  ],
  (
    _graph,
    nodeConnectorState,
    nodeContainerState,
    isEditingLinkedChildAction,
    hasNodeContainers,
  ) => {
    const { ownerNodeId } = nodeConnectorState;
    const { hasNodeFolder, hasSnippets } = hasNodeContainers;

    const graph = hasNodeFolder ? appendFolderNode(cloneDeep(_graph)) : _graph;

    const organizedGraph = (hasNodeFolder || hasSnippets)
      ? organizeContainerNodes(cloneDeep(graph), nodeContainerState)
      : graph;

    if (!shouldAddSkeletonNodeAndEdge(nodeConnectorState, isEditingLinkedChildAction)) {
      return organizedGraph;
    }

    return appendAdditionalGraphElements(
      cloneDeep(organizedGraph),
      { ownerNodeId },
    );
  },
);

const selectLayOutDependentUIState = createSelector(
  [
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.activeNodeEditorTab,
    (state: State) => state.workflowEditor.ui.floatedAnchorNodeId,
    (state: State) => state.workflowEditor.ui.canvasProperties.rect.width,
    (state: State) => state.workflowEditor.ui.highlightedNodeId,
    (state: State) => state.workflowEditor.ui.hoveredNodeId,
    selectNodeFolderState,
  ],
  (
    focusedNodeId,
    activeNodeEditorTab,
    floatedAnchorNodeId,
    canvasWidth,
    highlightedNodeId,
    hoveredNodeId,
    nodeFolder,
  ) => ({
    focusedNodeId,
    activeNodeEditorTab,
    floatedAnchorNodeId,
    canvasWidth,
    highlightedNodeId,
    hoveredNodeId,
    nodeFolder,
  }),
);

const selectAnchorNodeId = createSelector(
  [
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    (state: State) => state.workflowEditor.ui.floatedAnchorNodeId,
  ], (focusedNodeId, floatedNodeId) => {
    if (focusedNodeId) return focusedNodeId;
    if (floatedNodeId) return floatedNodeId;
    return null;
  });

export const selectGraphTopology = createSelector(
  [
    selectGraphWithAdditionalElements,
    selectNodeContainerState,
  ], (
    graph,
    nodeContainerState,
  ) => getBidirectionalGraphTopology(graph, nodeContainerState),
);


export const selectFolderSize = createSelector(
  [
    selectGraphWithAdditionalElements,
    selectNodeFolderState,
  ],
  (graph, nodeFolderState) => getFolderSize(nodeFolderState, graph.nodes),
);

export const selectSubgraphDimensions = createSelector(
  [
    selectGraph,
    selectNodeContainerState,
    selectFolderSize,
  ], (graph, collapsedStates, folderSize) => {
    const subgraphs = buildSnippetSubgraphs(graph);
    const topologies = buildSnippetSubgraphTopolgies(subgraphs);
    const incomers = buildSnippetSubgraphIncomersMap(topologies);

    const maxPathMaps = buildSnippetSubgraphMaxPathMaps(incomers, subgraphs);

    const { isCollapsed } = collapsedStates.inputAttribute;
    const folderDimensions = isCollapsed
      ? defaultNodeDimensions
      : { nodeHeight: folderSize + 1, nodeWidth: 1 };

    return {
      ...buildSubgraphDimensions(maxPathMaps, collapsedStates.snippets),
      [FOLDER_NODE_ID.inputAttribute]: folderDimensions,
    };
  },
);

export function selectSubgraphDimensionsById(publicId: PublicId) {
  return function selector(state: State) {
    const subGraphMaxPathMaps = selectSubgraphDimensions(state);
    return subGraphMaxPathMaps[publicId];
  };
}

const selectRenderPathIncomers = createSelector(
  [
    selectGraphWithAdditionalElements,
    selectGraphTopology,
  ],
  (graph, graphTopology) => (
    adjustSnippetChildIncomers({ graph, incomers: graphTopology.incomers })
  ),
);

const selectMaxIncomingPathLengthMap = createSelector(
  [
    selectRenderPathIncomers,
    selectGraphWithAdditionalElements,
    selectSubgraphDimensions,
  ],
  (incomers, graph, subgraphDimensions) => (
    getMaxPathLengthMap(
      {
        nodes: graph.nodes,
        incomers,
        subgraphDimensions,
      },
    )
  ),
);

export const selectNodeLineageForAnchorNode = createSelector(
  [
    selectGraphTopology,
    selectAnchorNodeId,
  ],
  (
    graphTopology,
    anchorNodeId,
  ) => getAllNodeIdsConnectedToNode(anchorNodeId, graphTopology),
);

export const selectUnsetTagNodeIds = createSelector([
  (state: State) => state.workflowEditor.workflowData?.actions,
  (state: State) => state.workflowEditor.workflowFeatures.ruleExplainability.setTagNames,
], (actions, setTagNames) => {
  if (actions && setTagNames) {
    return Object
      .values(actions)
      .reduce((acc, { publicId, tag }) => {
        if (tag && !setTagNames.includes(tag.name)) {
          acc.push(publicId);
        }
        return acc;
      }, []);
  }
  return [];
});

export const selectCriticalNodePathGraph = createSelector(
  [
    selectGraph,
    selectGraphTopology,
    selectShouldRenderCriticalNodePath,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
    selectUnsetTagNodeIds,
  ],
  (
    _graph,
    graphTopology,
    shouldRenderCriticalNodePath,
    focusedNodeId,
    unsetTagNodeIds,
  ): Graph | null => {
    if (!shouldRenderCriticalNodePath) {
      return null;
    }

    const focusedTagNodeLineage = focusedNodeId
      ? getAllNodeIdsConnectedToNode(
        focusedNodeId,
        graphTopology,
      )
      : {
        nodeId: null,
        directIncomers: new Set<string>(),
        directOutgoers: new Set<string>(),
        extendedIncomers: new Set<string>(),
        extendedOutgoers: new Set<string>(),
        all: new Set(_graph.nodes.map(node => node.id)),
      };

    return {
      nodes: filterNodesByLineage(
        filterOutUnsetTagNodes(_graph.nodes, unsetTagNodeIds),
        focusedTagNodeLineage,
      ),
      edges: filterEdgesByLineage(
        filterOutUnsetTagEdges(_graph.edges, unsetTagNodeIds),
        focusedTagNodeLineage,
      ),
    };
  },
);

export const selectLayOutedGraph = createSelector(
  [
    selectGraphWithAdditionalElements,
    selectMaxIncomingPathLengthMap,
    selectSubgraphDimensions,
    selectNodeLineageForAnchorNode,
    selectLayOutDependentUIState,
    selectHasNodeContainers,
    selectCriticalNodePathGraph,
  ],
  (
    _graph,
    maxIncomingPathLengthMap,
    subGraphDimensions,
    nodeLineage,
    uiStatePartial,
    { hasNodeFolder },
    _criticalNodePathGraph,
  ) => {
    const graph = cloneDeep(_criticalNodePathGraph || _graph);

    layOutGraph({
      graph,
      uiStatePartial,
      nodeLineage,
      maxIncomingPathLengthMap,
      subGraphDimensions,
      hasInputAttributeNodeFolder: hasNodeFolder,
    });

    return graph;
  });

const selectLayoutAdjustedGraph = createSelector(
  [
    selectLayOutedGraph,
    selectSnippetCollapsedStates,
  ],
  (_graph, snippetCollapsedStates): Graph => {
    const graph = cloneDeep(_graph);

    const edges = adjustEdgesForSnippetNodes(graph, snippetCollapsedStates);

    return { nodes: graph.nodes, edges };
  },
);

const selectCanvasAdjustedGraph = createSelector(
  [
    selectLayoutAdjustedGraph,
    (state: State) => state.workflowEditor.ui.canvasProperties.rect,
    (state: State) => state.workflowEditor.ui.focusedNodeId,
  ],
  (_graph, canvasRect, focusedNodeId) => {
    const graph = cloneDeep(_graph);

    adjustConstructionNodes({ graph, canvasRect, focusedNodeId });
    adjustLayoutToFreezeFixedNodes(graph, canvasRect);

    return graph;
  },
);

export const selectWorkflowGraph = createSelector(
  [
    selectCanvasAdjustedGraph,
  ],
  graph => graph,
);

export const selectFirstTagNodeId = createSelector(
  [
    selectWorkflowGraph,
  ],
  graph => {
    const tagNodes = graph.nodes
      .filter(node => node.type === 'tag')
      .sort((a, b) => {
        // Sort by visual position: first by x coordinate (left to right), then by y coordinate (top to bottom)
        if (a.position.x !== b.position.x) {
          return a.position.x - b.position.x;
        }
        return a.position.y - b.position.y;
      });

    return tagNodes.length > 0 ? tagNodes[0].id : null;
  },
);

export const selectIsWorkflowGraphEmpty = createSelector(
  [
    selectGraph,
  ],
  graph => graph.nodes.length === 0,
);
